import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Badge,
  Row,
  Col,
  Button,
  Form,
  InputGroup,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaSignature,
  FaFileExcel,
  FaSearch,
  FaDownload,
} from "react-icons/fa";
import {
  collection,
  query,
  where,
  getDocs,
  getFirestore,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

function UsuariosConFirma({ usuario }) {
  const [usuariosConFirma, setUsuariosConFirma] = useState([]);
  const [usuariosSinFirma, setUsuariosSinFirma] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTermConFirma, setSearchTermConFirma] = useState("");
  const [searchTermSinFirma, setSearchTermSinFirma] = useState("");

  useEffect(() => {
    if (usuario?.ruc) {
      obtenerDatosUsuariosYFirmas();
    }
  }, [usuario]);

  const obtenerDatosUsuariosYFirmas = async () => {
    setLoading(true);
    setError("");

    try {
      // Obtener todos los usuarios activos de la empresa
      const qUsuarios = query(
        collection(db, "users"),
        where("rucEmpresa", "==", usuario.ruc),
        where("activo", "==", true)
      );
      const usuariosSnapshot = await getDocs(qUsuarios);
      const todosUsuarios = [];
      usuariosSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.dni) {
          todosUsuarios.push({
            id: doc.id,
            ...userData,
          });
        }
      });

      // Obtener todas las firmas activas de la empresa
      const qFirmas = query(
        collection(db, "firmas"),
        where("ruc", "==", usuario.ruc),
        where("activo", "==", true)
      );
      const firmasSnapshot = await getDocs(qFirmas);
      const firmasActivas = {};
      firmasSnapshot.forEach((doc) => {
        const firmaData = doc.data();
        if (firmaData.dni) {
          firmasActivas[firmaData.dni] = firmaData;
        }
      });

      // Separar usuarios con y sin firma
      const conFirma = [];
      const sinFirma = [];

      todosUsuarios.forEach((usuario) => {
        if (firmasActivas[usuario.dni]) {
          conFirma.push({
            ...usuario,
            firma: firmasActivas[usuario.dni],
          });
        } else {
          sinFirma.push(usuario);
        }
      });

      setUsuariosConFirma(conFirma);
      setUsuariosSinFirma(sinFirma);
    } catch (err) {
      console.error("Error al obtener datos:", err);
      setError("Error al cargar los datos de usuarios y firmas");
    } finally {
      setLoading(false);
    }
  };

  // Función para exportar usuarios con firma a Excel
  const exportarConFirmaExcel = () => {
    if (usuariosConFirma.length === 0) {
      alert("No hay datos para exportar");
      return;
    }

    try {
      // Preparar datos para Excel
      const datosExcel = usuariosConFirma.map((usuario, index) => ({
        "N°": index + 1,
        DNI: usuario.dni,
        Nombres: usuario.name || "N/A",
        Apellidos: usuario.apellidos || "N/A",
        Cargo: usuario.cargo || "N/A",
        "Fecha de Firma": usuario.firma?.fechaDoc
          ? usuario.firma.fechaDoc.toDate().toLocaleDateString("es-PE")
          : "N/A",
        Estado: "Con Firma",
      }));

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 5 }, // N°
        { wch: 12 }, // DNI
        { wch: 20 }, // Nombres
        { wch: 20 }, // Apellidos
        { wch: 25 }, // Cargo
        { wch: 20 }, // Fecha de Firma
        { wch: 15 }, // Estado
      ];
      ws["!cols"] = colWidths;

      // Añadir hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, "Usuarios Con Firma");

      // Descargar archivo
      const fechaActual = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `Usuarios_Con_Firma_${fechaActual}.xlsx`);

      alert(`Excel descargado exitosamente con ${usuariosConFirma.length} registro(s)`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al generar el archivo Excel");
    }
  };

  // Función para exportar usuarios sin firma a Excel
  const exportarSinFirmaExcel = () => {
    if (usuariosSinFirma.length === 0) {
      alert("No hay datos para exportar");
      return;
    }

    try {
      // Preparar datos para Excel
      const datosExcel = usuariosSinFirma.map((usuario, index) => ({
        "N°": index + 1,
        DNI: usuario.dni,
        Nombres: usuario.name || "N/A",
        Apellidos: usuario.apellidos || "N/A",
        Cargo: usuario.cargo || "N/A",
        Estado: "Sin Firma",
      }));

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 5 }, // N°
        { wch: 12 }, // DNI
        { wch: 20 }, // Nombres
        { wch: 20 }, // Apellidos
        { wch: 25 }, // Cargo
        { wch: 15 }, // Estado
      ];
      ws["!cols"] = colWidths;

      // Añadir hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, "Usuarios Sin Firma");

      // Descargar archivo
      const fechaActual = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `Usuarios_Sin_Firma_${fechaActual}.xlsx`);

      alert(`Excel descargado exitosamente con ${usuariosSinFirma.length} registro(s)`);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      alert("Error al generar el archivo Excel");
    }
  };

  // Función para filtrar usuarios con firma por DNI
  const filtrarUsuariosConFirma = () => {
    if (!searchTermConFirma) return usuariosConFirma;
    return usuariosConFirma.filter((usuario) =>
      usuario.dni.toLowerCase().includes(searchTermConFirma.toLowerCase())
    );
  };

  // Función para filtrar usuarios sin firma por DNI
  const filtrarUsuariosSinFirma = () => {
    if (!searchTermSinFirma) return usuariosSinFirma;
    return usuariosSinFirma.filter((usuario) =>
      usuario.dni.toLowerCase().includes(searchTermSinFirma.toLowerCase())
    );
  };

  const totalUsuarios = usuariosConFirma.length + usuariosSinFirma.length;
  const usuariosConFirmaFiltrados = filtrarUsuariosConFirma();
  const usuariosSinFirmaFiltrados = filtrarUsuariosSinFirma();

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              borderBottom: "3px solid #f59e0b",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaSignature className="me-3" />
                Usuarios con Firma Electrónica
              </h2>
              <p className="mb-0 opacity-75">
                Gestión y control de firmas electrónicas de usuarios activos
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de carga para operaciones */}
            {loading && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                style={{ zIndex: 1080 }}
              >
                <div className="text-center">
                  <Spinner
                    animation="border"
                    variant="light"
                    style={{ width: "3rem", height: "3rem" }}
                  />
                  <p className="text-white mt-3 mb-0">
                    Cargando información de usuarios...
                  </p>
                  <p className="text-white-50 small">
                    Obteniendo datos de firmas electrónicas
                  </p>
                </div>
              </div>
            )}

            {/* Alertas */}
            {error && (
              <Alert 
                variant="danger" 
                onClose={() => setError("")} 
                dismissible
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "15px" }}
              >
                {error}
              </Alert>
            )}

            {/* Barra superior con información */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                      }}
                    >
                      <FaSignature size={24} />
                    </div>
                  </Col>
                  <Col md={11}>
                    <h5 className="mb-1 fw-semibold">
                      Control de Firmas Electrónicas
                    </h5>
                    <p className="text-muted mb-0 small">
                      Visualiza el estado de las firmas electrónicas de todos los usuarios activos de la empresa
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Estadísticas rápidas */}
            {!loading && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaUsers size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{totalUsuarios}</h4>
                      <p className="mb-0">Total de Usuarios</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaCheckCircle size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{usuariosConFirma.length}</h4>
                      <p className="mb-0">Con Firma Electrónica</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaTimesCircle size="2rem" className="mb-2" />
                      <h4 className="fw-bold">{usuariosSinFirma.length}</h4>
                      <p className="mb-0">Sin Firma Electrónica</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm"
                    style={{ borderRadius: "15px" }}
                  >
                    <Card.Body
                      className="text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                        color: "white",
                        borderRadius: "15px",
                      }}
                    >
                      <FaSignature size="2rem" className="mb-2" />
                      <h4 className="fw-bold">
                        {totalUsuarios > 0 ? Math.round((usuariosConFirma.length / totalUsuarios) * 100) : 0}%
                      </h4>
                      <p className="mb-0">Cobertura de Firmas</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Tabs con diseño premium */}
            {!loading && (
              <Card 
                className="border-0 shadow-sm"
                style={{ borderRadius: "15px" }}
              >
                <Tabs 
                  defaultActiveKey="con-firma" 
                  className="nav-tabs-custom"
                  style={{
                    background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                    borderRadius: "15px 15px 0 0",
                    padding: "15px 15px 0 15px",
                    borderBottom: "1px solid #dee2e6"
                  }}
                >
                  <Tab
                    eventKey="con-firma"
                    title={
                      <span className="d-flex align-items-center">
                        <FaCheckCircle className="me-2 text-success" />
                        Con Firma
                        <Badge 
                          bg="success" 
                          className="ms-2"
                          style={{ borderRadius: "8px" }}
                        >
                          {usuariosConFirma.length}
                        </Badge>
                      </span>
                    }
                  >
                    <div className="p-4">
                      {/* Controles superiores */}
                      <Row className="mb-3">
                        <Col md={6}>
                          <InputGroup>
                            <InputGroup.Text 
                              style={{ 
                                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                color: "white",
                                border: "none"
                              }}
                            >
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Buscar por DNI..."
                              value={searchTermConFirma}
                              onChange={(e) => setSearchTermConFirma(e.target.value)}
                              style={{ 
                                borderRadius: "0 10px 10px 0",
                                border: "1px solid #dee2e6"
                              }}
                            />
                          </InputGroup>
                        </Col>
                        <Col md={6} className="text-end">
                          {usuariosConFirma.length > 0 && (
                            <Button
                              onClick={exportarConFirmaExcel}
                              className="d-flex align-items-center gap-2 ms-auto"
                              style={{
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                                border: "none",
                                boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                              }}
                            >
                              <FaDownload size="14px" />
                              Exportar Excel ({usuariosConFirma.length})
                            </Button>
                          )}
                        </Col>
                      </Row>

                      {/* Tabla de usuarios con firma */}
                      {usuariosConFirmaFiltrados.length > 0 ? (
                        <div className="table-responsive" style={{ borderRadius: "10px", overflow: "hidden" }}>
                          <Table
                            hover
                            className="align-middle bg-white mb-0"
                            style={{ fontSize: "0.9rem" }}
                          >
                            <thead
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderBottom: "2px solid #dee2e6"
                              }}
                            >
                              <tr>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">#</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">DNI</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Nombres</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Apellidos</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Cargo</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Fecha de Firma</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usuariosConFirmaFiltrados.map((usuario, index) => (
                                <tr 
                                  key={usuario.dni}
                                  style={{ borderBottom: "1px solid #f1f3f4" }}
                                >
                                  <td className="text-center py-3 px-3">
                                    <Badge
                                      bg="dark"
                                      className="px-2 py-1"
                                      style={{ fontSize: "0.75rem", borderRadius: "8px" }}
                                    >
                                      {index + 1}
                                    </Badge>
                                  </td>
                                  <td className="fw-bold py-3 px-3">{usuario.dni}</td>
                                  <td className="py-3 px-3">{usuario.name || "N/A"}</td>
                                  <td className="py-3 px-3">{usuario.apellidos || "N/A"}</td>
                                  <td className="py-3 px-3 text-muted small">{usuario.cargo || "N/A"}</td>
                                  <td className="py-3 px-3 text-muted small">
                                    {usuario.firma?.fechaDoc
                                      ? usuario.firma.fechaDoc.toDate().toLocaleDateString("es-PE")
                                      : "N/A"}
                                  </td>
                                  <td className="text-center py-3 px-3">
                                    <Badge 
                                      bg="success"
                                      style={{ borderRadius: "8px" }}
                                    >
                                      <FaCheckCircle className="me-1" size="12px" />
                                      Con Firma
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <FaCheckCircle size="4rem" className="text-muted mb-3" />
                          <h5 className="text-muted fw-semibold">
                            {searchTermConFirma
                              ? "No se encontraron usuarios con ese DNI"
                              : "No hay usuarios con firma electrónica"}
                          </h5>
                          <p className="text-muted">
                            {searchTermConFirma
                              ? "Intenta con otro DNI"
                              : "Los usuarios con firma aparecerán aquí cuando la tengan registrada."}
                          </p>
                        </div>
                      )}
                    </div>
                  </Tab>

                  <Tab
                    eventKey="sin-firma"
                    title={
                      <span className="d-flex align-items-center">
                        <FaTimesCircle className="me-2 text-danger" />
                        Sin Firma
                        <Badge 
                          bg="danger" 
                          className="ms-2"
                          style={{ borderRadius: "8px" }}
                        >
                          {usuariosSinFirma.length}
                        </Badge>
                      </span>
                    }
                  >
                    <div className="p-4">
                      {/* Controles superiores */}
                      <Row className="mb-3">
                        <Col md={6}>
                          <InputGroup>
                            <InputGroup.Text 
                              style={{ 
                                background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                                color: "white",
                                border: "none"
                              }}
                            >
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Buscar por DNI..."
                              value={searchTermSinFirma}
                              onChange={(e) => setSearchTermSinFirma(e.target.value)}
                              style={{ 
                                borderRadius: "0 10px 10px 0",
                                border: "1px solid #dee2e6"
                              }}
                            />
                          </InputGroup>
                        </Col>
                        <Col md={6} className="text-end">
                          {usuariosSinFirma.length > 0 && (
                            <Button
                              onClick={exportarSinFirmaExcel}
                              className="d-flex align-items-center gap-2 ms-auto"
                              style={{
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                                border: "none",
                                boxShadow: "0 4px 8px rgba(16, 185, 129, 0.3)",
                              }}
                            >
                              <FaDownload size="14px" />
                              Exportar Excel ({usuariosSinFirma.length})
                            </Button>
                          )}
                        </Col>
                      </Row>

                      {/* Tabla de usuarios sin firma */}
                      {usuariosSinFirmaFiltrados.length > 0 ? (
                        <div className="table-responsive" style={{ borderRadius: "10px", overflow: "hidden" }}>
                          <Table
                            hover
                            className="align-middle bg-white mb-0"
                            style={{ fontSize: "0.9rem" }}
                          >
                            <thead
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderBottom: "2px solid #dee2e6"
                              }}
                            >
                              <tr>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">#</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">DNI</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Nombres</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Apellidos</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Cargo</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usuariosSinFirmaFiltrados.map((usuario, index) => (
                                <tr 
                                  key={usuario.dni}
                                  style={{ borderBottom: "1px solid #f1f3f4" }}
                                >
                                  <td className="text-center py-3 px-3">
                                    <Badge
                                      bg="dark"
                                      className="px-2 py-1"
                                      style={{ fontSize: "0.75rem", borderRadius: "8px" }}
                                    >
                                      {index + 1}
                                    </Badge>
                                  </td>
                                  <td className="fw-bold py-3 px-3">{usuario.dni}</td>
                                  <td className="py-3 px-3">{usuario.name || "N/A"}</td>
                                  <td className="py-3 px-3">{usuario.apellidos || "N/A"}</td>
                                  <td className="py-3 px-3 text-muted small">{usuario.cargo || "N/A"}</td>
                                  <td className="text-center py-3 px-3">
                                    <Badge 
                                      bg="danger"
                                      style={{ borderRadius: "8px" }}
                                    >
                                      <FaTimesCircle className="me-1" size="12px" />
                                      Sin Firma
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <FaTimesCircle size="4rem" className="text-muted mb-3" />
                          <h5 className="text-muted fw-semibold">
                            {searchTermSinFirma
                              ? "No se encontraron usuarios con ese DNI"
                              : "Todos los usuarios tienen firma electrónica"}
                          </h5>
                          <p className="text-muted">
                            {searchTermSinFirma
                              ? "Intenta con otro DNI"
                              : "¡Excelente! Todos los usuarios de la empresa han registrado su firma."}
                          </p>
                        </div>
                      )}
                    </div>
                  </Tab>
                </Tabs>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default UsuariosConFirma;